import {
  Calculator,
  PiggyBank,
  FileUp,
  Activity,
  ArrowRightLeft,
  Paintbrush,
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowUpFromLine,
  BadgeDollarSign,
  BarChart3,
  Bell,
  BookOpen,
  Boxes,
  Briefcase,
  Building2,
  CalendarClock,
  CarFront,
  ClipboardList,
  Snowflake,
  FileStack,
  Coins,
  Component,
  Contact,
  Container,
  CreditCard,
  FileSpreadsheet,
  FileText,
  Fingerprint,
  FolderTree,
  Gauge,
  GitCompareArrows,
  Globe,
  Handshake,
  IdCard,
  Kanban,
  KeyRound,
  Landmark,
  House,
  LayoutDashboard,
  Grid3x3,
  Layers,
  Link2,
  ListChecks,
  ListTree,
  MapPin,
  MessagesSquare,
  MonitorCog,
  Network,
  Package,
  PackageCheck,
  PackageOpen,
  Receipt,
  Repeat,
  Route,
  Scale,
  ScrollText,
  Ship,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  Stamp,
  Store,
  TrendingUp,
  Truck,
  Undo2,
  UserCog,
  Users,
  Wallet,
  Warehouse,
  type LucideIcon,
} from 'lucide-react'
import type { ComponentType } from 'react'
import type { TKey, Translate } from '@/i18n'

/**
 * Readiness mirrors the backend readiness matrix (ADMIN-FRONTEND-BLUEPRINT §12):
 *  - ready:        deployed typed API, integrated against the real backend
 *  - transitional: deployed, with a contract change isolated in its service adapter
 *  - planned:      UI designed, backend contract pending — disabled in the sidebar,
 *                  opened only as a clearly-labelled preview with no data
 */
export type Readiness = 'ready' | 'transitional' | 'planned'

/** All strings are translation keys: columns/filters → glossary.*, views → planned.views.*, etc. */
export type PlannedPreview = {
  kind: 'list' | 'report' | 'board'
  /** Glossary keys; a trailing "#" marks a right-aligned numeric column. */
  columns: string[]
  filters?: string[]
  views?: string[]
  primaryAction?: string
  note?: string
}

/** Labels are not stored here: they resolve from nav.items.<id> in the active locale. */
export type NavItem = {
  id: string
  path: string
  icon: LucideIcon
  readiness: Readiness
  /** Capability required to see and open the page. Omit for pages open to every signed-in profile. */
  capability?: string
  /** At least one of these capabilities is required. Used by pages with independently granted children. */
  anyCapability?: string[]
  /**
   * Platform administration (/v1/platform/*): shown and opened only when
   * /me/context says is_platform_admin. No company role grants it.
   */
  platformAdmin?: boolean
  load?: () => Promise<{ default: ComponentType }>
  preview?: PlannedPreview
  /** Routed but not listed in the sidebar (detail pages). */
  hidden?: boolean
  /**
   * Kept only by a company with batches of its own, the companies with the
   * reports' «Партия» filter (keepsBatches: an importer, AFF or ChF): hidden
   * and refused while any other company is chosen in the header («Свод
   * партий», client ALFI TEST #460 and #489).
   */
  batchesOnly?: boolean
  /** Joined into another page: routed for old links, and its server menu entry is not shown. */
  retired?: boolean
  keywords?: string
  /** An alias (an old address) shares the docs of the page it stands for: its id here. */
  docsKey?: string
  /** The docs pages themselves: no «?» and no docs of their own. */
  isDocs?: boolean
}

/** `label` is a nav.groups.* key. */
export type NavGroup = { label?: string; items: NavItem[] }

export type NavModule = {
  id: string
  icon: LucideIcon
  groups: NavGroup[]
}

const planned = (preview: PlannedPreview) => ({ readiness: 'planned' as const, preview })

export const navigation: NavModule[] = [
  {
    id: 'overview',
    icon: LayoutDashboard,
    groups: [
      {
        items: [
          {
            id: 'dashboard',
            path: '/',
            // A house, so the start page never looks like «Общие» (a grid) in the rail.
            icon: House,
            readiness: 'ready',
            load: () => import('@/features/dashboard/DashboardPage'),
          },
          {
            // Server menu item «chat» (migration 182); the top bar's chat icon opens the same chats in a drawer.
            id: 'chats',
            path: '/chats',
            icon: MessagesSquare,
            readiness: 'ready',
            capability: 'chat.use',
            keywords: 'chat messages group чат сообщения группа xabar guruh',
            load: () => import('@/features/chat/ChatsPage'),
          },
          {
            // Every page's «?» opens its docs here (src/docs/<pageKey>/); open to every signed-in profile.
            id: 'docs',
            path: '/docs/:pageKey',
            icon: BookOpen,
            readiness: 'ready',
            hidden: true,
            isDocs: true,
            load: () => import('@/features/docs/DocsPage'),
          },
          {
            id: 'docs-index',
            path: '/docs',
            icon: BookOpen,
            readiness: 'ready',
            hidden: true,
            isDocs: true,
            keywords: 'docs help manual documentation справка документация инструкция hujjat yordam qo\'llanma',
            load: () => import('@/features/docs/DocsPage').then((module) => ({ default: module.DocsIndexPage })),
          },
        ],
      },
    ],
  },
  {
    id: 'sales',
    icon: ShoppingCart,
    groups: [
      {
        items: [
          {
            id: 'orders',
            path: '/sales/orders',
            icon: Receipt,
            readiness: 'ready',
            capability: 'sales_orders.read',
            load: () => import('@/features/sales/orders/OrdersPage'),
          },
          {
            id: 'order-board',
            path: '/sales/board',
            icon: Kanban,
            readiness: 'ready',
            capability: 'sales_orders.read',
            load: () => import('@/features/sales/orders/OrdersPage').then((module) => ({ default: module.OrderBoardPage })),
          },
          {
            id: 'requests',
            path: '/sales/requests',
            icon: ListChecks,
            readiness: 'ready',
            capability: 'sales_orders.read',
            load: () => import('@/features/sales/orders/OrdersPage').then((module) => ({ default: module.OrderRequestsPage })),
          },
          {
            id: 'clients',
            path: '/sales/clients',
            icon: Contact,
            readiness: 'ready',
            capability: 'clients.read',
            load: () => import('@/features/sales/clients/ClientsPage'),
          },
          {
            // The old address of «Эффективность», now under Финансы › Отчёты; kept so saved links open.
            id: 'agent-effectiveness-old',
            path: '/sales/agent-effectiveness',
            icon: TrendingUp,
            readiness: 'ready',
            capability: 'agent_effectiveness.read',
            hidden: true,
            docsKey: 'agent-effectiveness',
            load: () => import('@/features/finance/effectiveness/EffectivenessPage'),
          },
          {
            id: 'work-tasks',
            path: '/sales/work-tasks',
            icon: ClipboardList,
            readiness: 'ready',
            capability: 'work_tasks.read',
            keywords: 'tasks cashier accountant задачи кассир бухгалтер vazifalar',
            load: () => import('@/features/sales/workTasks/WorkTasksPage'),
          },
        ],
      },
    ],
  },
  {
    id: 'procurement',
    icon: Ship,
    groups: [
      {
        label: 'operations',
        items: [
          {
            id: 'plans',
            path: '/procurement/plans',
            icon: CalendarClock,
            readiness: 'ready',
            capability: 'procurement_plans.read',
            load: () => import('@/features/procurement/PlansPage'),
          },
          {
            id: 'proformas',
            path: '/procurement/proformas',
            icon: FileText,
            readiness: 'ready',
            capability: 'procurement_proformas.read',
            load: () => import('@/features/procurement/ProformasPage'),
          },
          {
            id: 'batches',
            path: '/procurement/batches',
            icon: Layers,
            readiness: 'ready',
            capability: 'procurement_batches.read',
            load: () => import('@/features/procurement/BatchesPage'),
          },
          {
            id: 'containers',
            path: '/procurement/containers',
            icon: Container,
            readiness: 'ready',
            capability: 'procurement_transport_units.read',
            load: () => import('@/features/procurement/ContainersPage'),
          },
          {
            id: 'transport',
            path: '/procurement/transport',
            icon: Truck,
            readiness: 'ready',
            capability: 'procurement_transport_units.read',
            load: () => import('@/features/procurement/TrucksPage'),
          },
          {
            id: 'procurement-movements',
            path: '/procurement/movements',
            icon: ArrowLeftRight,
            readiness: 'ready',
            capability: 'procurement_movements.read',
            load: () => import('@/features/procurement/MovementsPage'),
          },
          {
            id: 'customs',
            path: '/procurement/customs',
            icon: Stamp,
            readiness: 'ready',
            capability: 'procurement_customs_declarations.read',
            load: () => import('@/features/procurement/CustomsDeclarationsPage'),
          },
          {
            // «Начисление» of a declaration: the customs breakdown on a procurement right,
            // so it opens whatever the role's Finance access (owner 2026-10-07).
            id: 'customs-breakdown',
            path: '/procurement/customs/:id/breakdown',
            icon: Stamp,
            readiness: 'ready',
            capability: 'procurement_customs_declarations.breakdown',
            hidden: true,
            load: () => import('@/features/reports/batchSummary/BatchCustomsPage'),
          },
        ],
      },
      {
        label: 'references',
        items: [
          {
            id: 'procurement-terms',
            path: '/procurement/payment-terms',
            icon: CreditCard,
            readiness: 'ready',
            capability: 'procurement_terms.read',
            load: () => import('@/features/procurement/PaymentTermsPage'),
          },
          {
            id: 'procurement-container-types',
            path: '/procurement/container-types',
            icon: Container,
            readiness: 'ready',
            capability: 'procurement_container_types.read',
            load: () => import('@/features/procurement/ContainerTypesPage'),
          },
          {
            id: 'procurement-status-templates',
            path: '/procurement/route-substatuses',
            icon: ListChecks,
            readiness: 'ready',
            capability: 'procurement_status_templates.read',
            load: () => import('@/features/procurement/StatusTemplatesPage'),
          },
          {
            // «Группы товаров» lives only under «Закупки → Справочники» (client #609, menu migration 126).
            id: 'product-groups',
            path: '/catalog/product-groups',
            icon: Component,
            readiness: 'ready',
            capability: 'product_groups.read',
            load: () => import('@/features/catalog/ProductGroupsPage'),
          },
        ],
      },
      {
        label: 'reports',
        items: [
          {
            id: 'procurement-debt',
            path: '/procurement/debts',
            icon: Scale,
            readiness: 'ready',
            capability: 'procurement_debts.read',
            load: () => import('@/features/procurement/DebtsPage'),
          },
          {
            id: 'procurement-reports',
            path: '/procurement/reports',
            icon: BarChart3,
            readiness: 'ready',
            anyCapability: [
              'procurement_reports.debt.read',
              'procurement_reports.reconciliation.read',
              'procurement_reports.order_balance.read',
              'procurement_reports.proforma_summary.read',
              'procurement_reports.in_transit_products.read',
              'procurement_reports.in_transit_status.read',
              'procurement_reports.in_transit_product_status.read',
              'procurement_reports.product_balance.read',
              'procurement_reports.customs_status.read',
              'procurement_reports.customs_debt.read',
              'procurement_reports.planner.read',
            ],
            load: () => import('@/features/procurement/ReportsPage'),
          },
        ],
      },
    ],
  },
  {
    id: 'warehouse',
    icon: Warehouse,
    groups: [
      {
        label: 'operations',
        items: [
          {
            id: 'receiving',
            path: '/warehouse/receiving',
            icon: ArrowDownToLine,
            readiness: 'ready',
            // One «Приёмка» (client #299): Tashkent's trucks and the branches' receivings of Tashkent's shipments.
            anyCapability: ['warehouse_receiving.read', 'warehouse_branch_receiving.read'],
            load: () => import('@/features/warehouse/documents/ReceivingsPage'),
          },
          {
            // Joined into «Приёмка» (client #299); kept routed for old links.
            id: 'branch-receiving',
            path: '/warehouse/branch-receiving',
            icon: PackageCheck,
            readiness: 'ready',
            hidden: true,
            retired: true,
            capability: 'warehouse_branch_receiving.read',
            load: () => import('@/features/warehouse/sales/BranchReceivingsPage'),
          },
          {
            id: 'shipments',
            path: '/warehouse/shipments',
            icon: ArrowUpFromLine,
            readiness: 'ready',
            capability: 'warehouse_shipments.read',
            load: () => import('@/features/warehouse/sales/ShipmentsPage'),
          },
          {
            id: 'returns',
            path: '/warehouse/returns',
            icon: Undo2,
            readiness: 'ready',
            capability: 'warehouse_returns.read',
            load: () => import('@/features/warehouse/sales/ReturnsPage'),
          },
          {
            id: 'transfers',
            path: '/warehouse/transfers',
            icon: ArrowLeftRight,
            readiness: 'ready',
            capability: 'warehouse_transfers.read',
            load: () => import('@/features/warehouse/documents/TransfersPage'),
          },
          {
            id: 'inventory',
            path: '/warehouse/inventory',
            icon: ClipboardList,
            readiness: 'ready',
            capability: 'warehouse_inventory.read',
            load: () => import('@/features/warehouse/documents/InventoriesPage'),
          },
          {
            id: 'freezing',
            path: '/warehouse/freezing',
            icon: Snowflake,
            readiness: 'ready',
            capability: 'warehouse_freezing.read',
            load: () => import('@/features/warehouse/documents/FreezingsPage'),
          },
          {
            // ALFI TEST #16: the agent and client catalog's «Нет в наличии» tick.
            id: 'product-availability',
            path: '/warehouse/product-availability',
            icon: PackageCheck,
            readiness: 'ready',
            capability: 'product_availability.read',
            keywords: 'availability in stock out of stock наличие нет в наличии доступность mavjud',
            load: () => import('@/features/catalog/ProductAvailabilityPage'),
          },
        ],
      },
      {
        label: 'stock',
        items: [
          {
            id: 'stock-balance',
            path: '/warehouse/reports',
            icon: Boxes,
            readiness: 'ready',
            anyCapability: [
              'warehouse_reports.stock_balance.read',
              'warehouse_reports.reserve.read',
              'warehouse_reports.carcass.read',
              'warehouse_reports.batch_stock.read',
              'warehouse_reports.turnover.read',
              'warehouse_reports.sales.read',
              'warehouse_reports.sales_dynamics.read',
              'warehouse_reports.shelf_life.read',
              'warehouse_reports.stock_dynamics.read',
              'warehouse_reports.in_transit.read',
            ],
            load: () => import('@/features/warehouse/reports/ReportsPage'),
          },
        ],
      },
      {
        label: 'references',
        items: [
          {
            id: 'warehouses',
            path: '/warehouse/warehouses',
            icon: Warehouse,
            readiness: 'ready',
            capability: 'warehouses.read',
            load: () => import('@/features/warehouse/WarehousesPage'),
          },
          {
            id: 'warehouse-cells',
            path: '/warehouse/cells',
            icon: Grid3x3,
            readiness: 'ready',
            capability: 'warehouse_cells.read',
            load: () => import('@/features/warehouse/WarehouseCellsPage'),
          },
          {
            id: 'fifo-cells',
            path: '/warehouse/fifo-cells',
            icon: Layers,
            readiness: 'ready',
            capability: 'warehouse_stock.read',
            load: () => import('@/features/warehouse/FifoCellsPage'),
          },
          {
            id: 'registers',
            path: '/warehouse/registers',
            icon: FileStack,
            readiness: 'ready',
            capability: 'warehouse_registers.read',
            load: () => import('@/features/warehouse/RegistersPage'),
          },
        ],
      },
    ],
  },
  {
    id: 'delivery',
    icon: Truck,
    groups: [
      {
        items: [
          {
            id: 'dispatch',
            path: '/delivery/orders',
            icon: Route,
            readiness: 'ready',
            capability: 'delivery_orders.read',
            anyCapability: ['sales_orders.read'],
            load: () => import('@/features/delivery/DeliveryOrdersPage'),
          },
          {
            id: 'tariffs',
            path: '/delivery/tariffs',
            icon: BadgeDollarSign,
            readiness: 'ready',
            capability: 'delivery_tariffs.read',
            load: () => import('@/features/delivery/TariffsPage'),
          },
          {
            id: 'delivery-report',
            path: '/delivery/report',
            icon: FileSpreadsheet,
            readiness: 'ready',
            capability: 'delivery_report.read',
            keywords: 'delivery report income expenses доставка отчет доходы расходы',
            load: () => import('@/features/delivery/DeliveryReportPage'),
          },
        ],
      },
      {
        label: 'garage',
        items: [
          {
            id: 'vehicles',
            path: '/delivery/vehicles',
            icon: CarFront,
            readiness: 'ready',
            capability: 'delivery_vehicles.read',
            load: () => import('@/features/delivery/VehiclesPage'),
          },
          {
            id: 'vehicle-types',
            path: '/delivery/vehicle-types',
            icon: Truck,
            readiness: 'ready',
            capability: 'delivery_vehicles.read',
            load: () => import('@/features/delivery/VehicleTypesPage'),
          },
        ],
      },
    ],
  },
  {
    id: 'finance',
    icon: Wallet,
    groups: [
      {
        items: [
          {
            id: 'finance-overview',
            path: '/finance/overview',
            icon: Gauge,
            ...planned({ kind: 'report', columns: ['cashbox', 'currency', 'opening#', 'income#', 'expense#', 'balance#'], filters: ['branch', 'date'] }),
          },
          {
            id: 'operations',
            path: '/finance/operations',
            icon: Repeat,
            readiness: 'ready',
            anyCapability: ['finance_receipts.read', 'finance_payments.read', 'finance_transfers.read', 'finance_accruals.read'],
            load: () => import('@/features/finance/OperationsPage'),
          },
          {
            id: 'bank-imports',
            path: '/finance/bank-imports',
            icon: FileUp,
            readiness: 'ready',
            capability: 'finance_bank_imports.read',
            keywords: 'bank statement import xlsx банковская выписка импорт',
            load: () => import('@/features/finance/bankImports/BankImportsPage'),
          },
          {
            id: 'reserves',
            path: '/finance/reserves',
            icon: PiggyBank,
            readiness: 'ready',
            capability: 'finance_reserves.read',
            keywords: 'reserve prepayment commission резерв предоплата комиссия',
            load: () => import('@/features/finance/ReservesPage'),
          },
          {
            id: 'personal-expenses',
            path: '/finance/personal-expenses',
            icon: Receipt,
            readiness: 'ready',
            capability: 'expenses.read',
            keywords: 'personal expenses agent личные расходы агент',
            load: () => import('@/features/finance/expenses/ExpensesPage'),
          },
          {
            // AGENTS > «Отчет по личным расходам»: the period's expenses per employee × article.
            id: 'personal-expense-report',
            path: '/finance/personal-expense-report',
            icon: FileSpreadsheet,
            readiness: 'ready',
            capability: 'agent_expense_report.read',
            keywords: 'personal expenses report agent отчет личные расходы агент hisobot',
            load: () => import('@/features/finance/expenses/ExpenseReportPage'),
          },
          {
            id: 'payments',
            path: '/finance/payments',
            icon: Coins,
            readiness: 'ready',
            capability: 'client_agent_entries.read',
            keywords: 'client payments agents debts платежи клиентов агентам долг to‘lovlar',
            load: () => import('@/features/finance/clientPayments/ClientPaymentsPage'),
          },
          {
            id: 'postings',
            path: '/finance/postings',
            icon: ScrollText,
            readiness: 'ready',
            capability: 'finance_postings.read',
            load: () => import('@/features/finance/PostingsPage'),
          },
          {
            id: 'pricelists',
            path: '/finance/pricelists',
            icon: FileSpreadsheet,
            readiness: 'ready',
            capability: 'price_lists.read',
            load: () => import('@/features/finance/priceLists/PriceListsPage'),
          },
          {
            // «Корректировка остатков филиалов» (client 2026-11-02): a Tashkent price change revalues the branches' stock into their debt.
            id: 'branch-price-corrections',
            path: '/finance/branch-price-corrections',
            icon: Calculator,
            readiness: 'ready',
            capability: 'branch_price_corrections.read',
            keywords: 'branch stock price correction revaluation discount корректировка остатков филиалов переоценка скидка прайс filial qoldiq tuzatish',
            load: () => import('@/features/finance/BranchPriceCorrectionsPage'),
          },
          {
            // Legacy FINANCE > CASHBOX: cashbox receipts, payments, exchanges and transfers.
            id: 'cashbox',
            path: '/finance/cashbox',
            icon: Wallet,
            readiness: 'ready',
            anyCapability: ['finance_receipts.read', 'finance_payments.read', 'finance_exchanges.read', 'finance_transfers.read'],
            keywords: 'cashbox cash касса наличные',
            load: () => import('@/features/finance/CashboxPage'),
          },
          {
            // «Терминал» (legacy FINANCE > Card transactions, Operations ?type=plastik): card receipts; right after «Касса».
            id: 'card-transactions',
            path: '/finance/card-transactions',
            icon: CreditCard,
            readiness: 'ready',
            capability: 'finance_receipts.read',
            keywords: 'terminal card plastic humo uzcard терминал пластик карта',
            load: () => import('@/features/finance/CardTransactionsPage'),
          },
          {
            // «Нетто пластик»: the accountant's card slips matched with the bank lines that paid them; posts the terminal commission.
            id: 'card-matching',
            path: '/finance/card-matching',
            icon: GitCompareArrows,
            readiness: 'ready',
            capability: 'finance_bank_imports.read',
            keywords: 'net plastic netto terminal commission match нетто пластик терминал комиссия сопоставление проценты',
            load: () => import('@/features/finance/CardMatchingPage'),
          },
        ],
      },
      {
        label: 'references',
        items: [
          {
            id: 'cashboxes',
            path: '/finance/cashboxes',
            icon: Landmark,
            readiness: 'ready',
            capability: 'cashboxes.read',
            load: () => import('@/features/finance/CashboxesPage'),
          },
          {
            id: 'chart-of-accounts',
            path: '/finance/accounts',
            icon: BookOpen,
            readiness: 'ready',
            anyCapability: ['finance_chart_accounts.read', 'finance_accounts.read'],
            load: () => import('@/features/finance/ChartOfAccountsPage'),
          },
          {
            id: 'currencies',
            path: '/finance/currencies',
            icon: Coins,
            readiness: 'ready',
            anyCapability: ['currencies.read', 'finance_exchange_rate_versions.read'],
            load: () => import('@/features/finance/CurrenciesPage'),
          },
          {
            // Balance groups (client alfi.uz, 2026-10-23): AFF, IML, CAMA, UZB LOGISTICS as separate books.
            id: 'balance-groups',
            path: '/finance/balance-groups',
            icon: Layers,
            readiness: 'ready',
            capability: 'finance_balance_groups.read',
            keywords: 'company companies balance group importer distributor trader logist компания компании баланс импортер дистрибьютор трейдер логист',
            load: () => import('@/features/finance/BalanceGroupsPage'),
          },
          {
            id: 'card-types',
            path: '/finance/card-types',
            icon: CreditCard,
            readiness: 'ready',
            capability: 'card_types.read',
            keywords: 'card type plastic humo uzcard тип пластика карта',
            load: () => import('@/features/finance/CardTypesPage'),
          },
          {
            // «Мультипликатор» (client 2026-10-24): a coefficient per product group (and factory) on the batch cost.
            id: 'cost-multipliers',
            path: '/finance/cost-multipliers',
            icon: Calculator,
            readiness: 'ready',
            capability: 'finance_cost_multipliers.read',
            keywords: 'multiplier coefficient adjusted cost мультипликатор коэффициент себестоимость скорр multiplikator koeffitsient',
            load: () => import('@/features/finance/CostMultipliersPage'),
          },
          {
            // Legacy FINANCE > Reference > Currency conversion (exchange_rates): a read over the exchange-rate versions.
            id: 'currency-conversion',
            path: '/finance/currency-conversion',
            icon: ArrowRightLeft,
            readiness: 'ready',
            capability: 'finance_exchange_rate_versions.read',
            keywords: 'currency conversion exchange rate конвертация курс валют',
            load: () => import('@/features/finance/CurrencyConversionPage'),
          },
          {
            id: 'expense-categories',
            path: '/finance/expense-categories',
            icon: ListTree,
            readiness: 'ready',
            capability: 'expense_categories.read',
            keywords: 'expense articles personal статьи личных расходов',
            load: () => import('@/features/finance/expenses/ExpenseCategoriesPage'),
          },
          {
            // Legacy FINANCE > Reference > Mapping (bank_tin_accounts): per-TIN bank statement defaults.
            id: 'bank-tin-mapping',
            path: '/finance/bank-tin-mapping',
            icon: Link2,
            readiness: 'ready',
            capability: 'finance_bank_imports.read',
            keywords: 'mapping tin inn bank statement сопоставление инн выписка',
            load: () => import('@/features/finance/BankTinMappingPage'),
          },
        ],
      },
    ],
  },
  {
    id: 'catalog',
    icon: Package,
    groups: [
      {
        items: [
          {
            id: 'products',
            path: '/catalog/products',
            icon: Package,
            readiness: 'ready',
            capability: 'products.read',
            load: () => import('@/features/catalog/ProductsPage'),
          },
          {
            id: 'categories',
            path: '/catalog/categories',
            icon: FolderTree,
            readiness: 'ready',
            capability: 'categories.read',
            load: () => import('@/features/catalog/CategoriesPage'),
          },
          {
            id: 'packages',
            path: '/catalog/packages',
            icon: PackageOpen,
            readiness: 'ready',
            capability: 'packages.read',
            load: () => import('@/features/catalog/PackagesPage'),
          },
        ],
      },
      {
        label: 'geography',
        items: [
          {
            id: 'countries',
            path: '/catalog/countries',
            icon: Globe,
            readiness: 'ready',
            capability: 'countries.read',
            load: () => import('@/features/catalog/CountriesPage'),
          },
          {
            id: 'locations',
            path: '/catalog/locations',
            icon: MapPin,
            readiness: 'ready',
            capability: 'locations.read',
            load: () => import('@/features/catalog/LocationsPage'),
          },
        ],
      },
    ],
  },
  {
    id: 'counterparties',
    icon: Handshake,
    groups: [
      {
        items: [
          {
            id: 'parties',
            path: '/counterparties/directory',
            icon: Network,
            readiness: 'ready',
            capability: 'other_counterparties.read',
            keywords: 'counterparties other kitchen контрагенты прочие кухня',
            load: () => import('@/features/finance/counterparties/OtherCounterpartiesPage'),
          },
          {
            id: 'partners',
            path: '/counterparties/partners',
            icon: Handshake,
            readiness: 'ready',
            capability: 'partners.read',
            load: () => import('@/features/parties/PartnersPage'),
          },
          {
            id: 'legal-entities',
            path: '/counterparties/legal-entities',
            icon: Building2,
            readiness: 'ready',
            capability: 'legal_entities.read',
            load: () => import('@/features/parties/LegalEntitiesPage'),
          },
          {
            id: 'party-ledger',
            path: '/counterparties/ledger',
            icon: Scale,
            readiness: 'ready',
            capability: 'finance_reconciliation.read',
            keywords: 'reconciliation statement mutual settlements carcass debt акт сверки взаиморасчёты долг по тушам агент клиент филиал',
            load: () => import('@/features/finance/reconciliation/ReconciliationPage'),
          },
        ],
      },
    ],
  },
  {
    id: 'hr',
    icon: IdCard,
    groups: [
      {
        items: [
          {
            id: 'employees',
            path: '/hr/employees',
            icon: Users,
            readiness: 'ready',
            capability: 'employee_settings.read',
            // «Лимиты агентов»: kept routed but out of the menu and search for now.
            hidden: true,
            load: () => import('@/features/sales/orders/AgentLimitsPage'),
          },
          {
            id: 'timesheet',
            path: '/hr/timesheet',
            icon: CalendarClock,
            readiness: 'ready',
            capability: 'timesheet.read',
            keywords: 'timesheet attendance visit табель посещаемость tabel',
            load: () => import('@/features/hr/timesheet/TimesheetPage'),
          },
          {
            id: 'salary',
            path: '/hr/salary',
            icon: BadgeDollarSign,
            readiness: 'ready',
            capability: 'payroll.read',
            keywords: 'salary payroll wages зарплата расчет оклад',
            load: () => import('@/features/hr/payroll/PayrollPage'),
          },
          {
            id: 'hr-staff',
            path: '/hr/staff',
            icon: UserCog,
            readiness: 'ready',
            capability: 'hr_employees.read',
            keywords: 'staff personnel employees register кадры сотрудники',
            load: () => import('@/features/hr/staff/StaffPage'),
          },
        ],
      },
      {
        label: 'references',
        items: [
          {
            id: 'job-titles',
            path: '/hr/job-titles',
            icon: Briefcase,
            readiness: 'ready',
            capability: 'job_titles.read',
            load: () => import('@/features/hr/JobTitlesPage'),
          },
          {
            id: 'departments',
            path: '/hr/departments',
            icon: Building2,
            readiness: 'ready',
            capability: 'departments.read',
            load: () => import('@/features/hr/DepartmentsPage'),
          },
          {
            id: 'work-locations',
            path: '/hr/work-locations',
            icon: MapPin,
            readiness: 'ready',
            capability: 'work_locations.read',
            load: () => import('@/features/hr/WorkLocationsPage'),
          },
        ],
      },
    ],
  },
  {
    id: 'reports',
    icon: BarChart3,
    groups: [
      {
        items: [
          {
            id: 'counterparty-balances',
            path: '/reports/counterparty-balances',
            icon: Network,
            readiness: 'ready',
            capability: 'finance_counterparties.read',
            // Hidden for everyone for now (backend migration 166 drops it from the menu); page and service kept.
            hidden: true,
            keywords: 'counterparties balances receivable payable debt контрагенты балансы дебиторка кредиторка сальдо',
            load: () => import('@/features/finance/counterparties/CounterpartiesPage'),
          },
          {
            id: 'balance-sheet',
            path: '/reports/balance-sheet',
            icon: Scale,
            readiness: 'ready',
            capability: 'finance_balance_report.read',
            keywords: 'balance sheet assets liabilities equity баланс активы пассивы',
            load: () => import('@/features/finance/reports/BalanceSheetPage'),
          },
          {
            id: 'profit-loss',
            path: '/reports/profit-loss',
            icon: TrendingUp,
            readiness: 'ready',
            capability: 'finance_profit_loss_report.read',
            keywords: 'profit loss income expenses P&L ОПиУ прибыль убыток',
            load: () => import('@/features/finance/reports/ProfitLossPage'),
          },
          {
            // Kept as "report-hub" for the legacy finance menu, where it is the cash flow report.
            id: 'report-hub',
            path: '/reports/cash-flow',
            icon: BarChart3,
            readiness: 'ready',
            capability: 'finance_cash_flow_report.read',
            keywords: 'cash flow ДДС движение денежных средств',
            load: () => import('@/features/finance/reports/CashFlowPage'),
          },
          {
            // «Изменения капитала» after «ДДС» (client, ALFI TEST #17).
            id: 'equity-changes',
            path: '/reports/equity-changes',
            icon: Landmark,
            readiness: 'ready',
            capability: 'finance_equity_changes_report.read',
            keywords: 'equity changes capital founders dividends изменения капитала капитал учредители дивиденды',
            load: () => import('@/features/finance/reports/EquityChangesPage'),
          },
          {
            // «Эффективность» (agents, clients, branches) moved here from Агенты (client, ALFI TEST #17).
            id: 'agent-effectiveness',
            path: '/reports/effectiveness',
            icon: TrendingUp,
            readiness: 'ready',
            capability: 'agent_effectiveness.read',
            keywords: 'effectiveness agents clients branches эффективность агенты клиенты филиалы samaradorlik',
            load: () => import('@/features/finance/effectiveness/EffectivenessPage'),
          },
          {
            // «Ценообразование» (client, ALFI TEST #17): full cost per product against the Tashkent price.
            id: 'pricing',
            path: '/reports/pricing',
            icon: Calculator,
            readiness: 'ready',
            capability: 'finance_pricing_report.read',
            keywords: 'pricing cost margin price ценообразование себестоимость маржа цена narx tannarx',
            load: () => import('@/features/finance/reports/PricingPage'),
          },
          {
            id: 'batch-summary',
            path: '/reports/batch-summary',
            batchesOnly: true,
            icon: Layers,
            readiness: 'ready',
            capability: 'batch_summary.read',
            keywords: 'batch summary costs revenue profit свод партий партия расходы доходы прибыль',
            load: () => import('@/features/reports/batchSummary/BatchSummaryPage'),
          },
          {
            id: 'batch-summary-detail',
            path: '/reports/batch-summary/:id',
            batchesOnly: true,
            icon: Layers,
            readiness: 'ready',
            capability: 'batch_summary.read',
            hidden: true,
            load: () => import('@/features/reports/batchSummary/BatchSummaryDetailPage'),
          },
          {
            id: 'batch-summary-sales',
            path: '/reports/batch-summary/:id/sales',
            batchesOnly: true,
            icon: Layers,
            readiness: 'ready',
            capability: 'batch_summary.sales_breakdown',
            hidden: true,
            load: () => import('@/features/reports/batchSummary/BatchSalesPage'),
          },
          {
            id: 'batch-summary-customs',
            path: '/reports/batch-summary/:id/customs',
            batchesOnly: true,
            icon: Layers,
            readiness: 'ready',
            capability: 'batch_summary.customs_breakdown',
            hidden: true,
            load: () => import('@/features/reports/batchSummary/BatchCustomsPage'),
          },
        ],
      },
    ],
  },
  {
    id: 'mobile',
    icon: Smartphone,
    groups: [
      {
        items: [
          {
            id: 'notifications',
            path: '/mobile/notifications',
            icon: Bell,
            readiness: 'ready',
            capability: 'mobile_notifications.read',
            keywords: 'notifications push news уведомления новости',
            load: () => import('@/features/mobile/NotificationsPage'),
          },
          {
            id: 'contact-us',
            path: '/mobile/contact-us',
            icon: MessagesSquare,
            readiness: 'ready',
            capability: 'mobile_contacts.read',
            keywords: 'contact us phone связаться с нами телефон',
            load: () => import('@/features/mobile/ContactsPage'),
          },
          {
            id: 'areas-of-activity',
            path: '/mobile/areas-of-activity',
            icon: Store,
            readiness: 'ready',
            capability: 'client_activities.read',
            keywords: 'area of activity business сфера деятельности',
            load: () => import('@/features/mobile/ActivitiesPage'),
          },
        ],
      },
    ],
  },
  {
    id: 'administration',
    icon: ShieldCheck,
    groups: [
      {
        label: 'identity',
        items: [
          {
            id: 'users',
            path: '/administration/users',
            icon: Users,
            readiness: 'ready',
            capability: 'users.read',
            keywords: 'employees accounts people',
            load: () => import('@/features/administration/users/UsersPage'),
          },
          {
            id: 'branches',
            path: '/administration/branches',
            icon: Building2,
            readiness: 'ready',
            capability: 'branches.read',
            load: () => import('@/features/administration/BranchesPage'),
          },
        ],
      },
      {
        label: 'access',
        items: [
          {
            id: 'roles',
            path: '/administration/roles',
            icon: KeyRound,
            readiness: 'transitional',
            capability: 'roles.read',
            keywords: 'permissions grants access',
            load: () => import('@/features/administration/roles/RolesPage'),
          },
          {
            id: 'role-detail',
            path: '/administration/roles/:id',
            icon: KeyRound,
            readiness: 'transitional',
            capability: 'roles.read',
            hidden: true,
            load: () => import('@/features/administration/roles/RoleDetailPage'),
          },
          {
            id: 'client-types',
            path: '/administration/client-types',
            // Hidden for everyone (ALFI TEST #35, backend migration 168 drops it from the menu); page and service kept.
            hidden: true,
            icon: UserCog,
            readiness: 'ready',
            capability: 'client_types.read',
            load: () => import('@/features/administration/ClientTypesPage'),
          },
          {
            id: 'access-resources',
            path: '/administration/access-resources',
            // Hidden for everyone (ALFI TEST #35, backend migration 168 drops it from the menu); page and service kept.
            hidden: true,
            icon: ListTree,
            readiness: 'ready',
            capability: 'access_resources.read',
            load: () => import('@/features/administration/AccessResourcesPage'),
          },
          {
            id: 'menu-items',
            path: '/administration/menu-items',
            // Hidden for everyone (ALFI TEST #35, backend migration 168 drops it from the menu); page and service kept.
            hidden: true,
            icon: ListTree,
            readiness: 'ready',
            capability: 'access_resources.read',
            load: () => import('@/features/administration/MenuItemsPage'),
          },
          {
            id: 'permissions',
            path: '/administration/permissions',
            // Hidden for everyone (ALFI TEST #35, backend migration 168 drops it from the menu); page and service kept.
            hidden: true,
            icon: Fingerprint,
            readiness: 'ready',
            capability: 'permissions.read',
            load: () => import('@/features/administration/PermissionsPage'),
          },
          {
            id: 'mobile-experiences',
            path: '/administration/mobile-experiences',
            // Hidden for everyone (ALFI TEST #35, backend migration 168 drops it from the menu); page and service kept.
            hidden: true,
            icon: Smartphone,
            readiness: 'transitional',
            capability: 'mobile_experiences.read',
            load: () => import('@/features/administration/MobileExperiencesPage'),
          },
        ],
      },
      {
        label: 'system',
        items: [
          {
            id: 'companies',
            path: '/administration/companies',
            icon: Briefcase,
            readiness: 'ready',
            platformAdmin: true,
            keywords: 'environments companies tenants platform среды среда компании тенанты',
            load: () => import('@/features/administration/CompaniesPage'),
          },
          {
            id: 'sessions',
            path: '/administration/sessions',
            icon: Activity,
            ...planned({ kind: 'list', columns: ['user', 'profile', 'client', 'ip', 'started', 'lastSeen', 'status'], filters: ['user', 'status'] }),
          },
          {
            id: 'security-audit',
            path: '/administration/audit',
            icon: MonitorCog,
            ...planned({ kind: 'list', columns: ['event', 'actor', 'target', 'result', 'ip', 'time'], filters: ['event', 'actor', 'period'] }),
          },
          {
            id: 'appearance',
            path: '/administration/appearance',
            icon: Paintbrush,
            readiness: 'ready',
            // Personal interface preference: no server capability required.
            load: () => import('@/features/system/AppearancePage'),
            keywords: 'appearance theme color font size radius background dark light оформление тема шрифт',
          },
        ],
      },
    ],
  },
]

export const allNavItems: NavItem[] = navigation.flatMap((module) => module.groups.flatMap((group) => group.items))

export const moduleOf = (item: NavItem) =>
  navigation.find((module) => module.groups.some((group) => group.items.includes(item)))!

function toPattern(path: string) {
  return new RegExp(`^${path.replace(/:[^/]+/g, '[^/]+')}/?$`)
}
const patterns = allNavItems.map((item) => ({ item, pattern: toPattern(item.path) }))

export function matchNavItem(pathname: string): NavItem | undefined {
  return patterns.find(({ pattern }) => pattern.test(pathname))?.item
}

/**
 * Starts downloading a page's code before it is opened (menu hover or focus),
 * so the click renders at once. The browser keeps each module after its first
 * load, so repeated calls cost nothing; a failed prefetch is simply retried on open.
 */
export function prefetchPage(pathname: string) {
  void matchNavItem(pathname)?.load?.().catch(() => undefined)
}

export const navLabel = (t: Translate, item: NavItem) => t(`nav.items.${item.id}` as TKey)
export const moduleLabel = (t: Translate, module: NavModule) => t(`nav.modules.${module.id}` as TKey)
export const groupLabel = (t: Translate, key: string) => t(`nav.groups.${key}` as TKey)
export const glossaryLabel = (t: Translate, key: string) => t(`glossary.${key.replace(/#$/, '')}` as TKey)
